// antd components
import { Form, Input, Select } from "antd";

// components - atom
import Button from "components/atoms/Button/Button";
import Label from "components/atoms/Label/Label";
import Spinner from "components/atoms/Spinner/Spinner";

// components - molecules
import ErrorMessage from "components/molecules/ErrorMessage/ErrorMessage";
import { Head } from "components/molecules/Head/Head";
import HeaderStatusInfo from "components/molecules/HeaderStatusInfo/HeaderStatusInfo";
import PageTitle from "components/molecules/PageTitle/PageTitle";

// constants
import { PageTitles, StatusInfo, AccountManagement as Constants } from "constants/global";

// hook
import { useCreateAccountPage } from "./useCreateAccountPage";

// styles
import styles from "containers/AccountManagement/CreateTenantAccount/createAccountPage.module.css";
import theme from "styles/theme";

// utils
import getIcon from "utils/getIcon";

const { Option } = Select;

type ErrorMessageWrapperTypes = {
	fieldError?: boolean;
	message: string;
};

const ErrorMessageWrapper = ({ fieldError, message }: ErrorMessageWrapperTypes) => {
	return fieldError ? <ErrorMessage classes={styles?.err_message} message={message} /> : <></>;
};

const CreateAccountPage = () => {
	const componentProps = useCreateAccountPage();
	const {
		goBack,
		onChange,
		onSearch,
		loading,
		storesDataList,
		searchInput,
		fieldErrors,
		validateFields,
		adminData,
		isCreateAdminButtonEnabled,
		proceedToCreateAdmin,
		handleChange
	} = componentProps;

	const usernameClasses = fieldErrors?.username ? styles?.err : styles?.filled;
	const emailClasses = fieldErrors?.email ? styles?.err : styles?.filled;

	return (
		<div className="min-h-screen relative">
			{loading && <Spinner type="transparent" />}
			<div className={styles?.bg_curve}>{getIcon({ icon: "landingPageBackground" })}</div>
			<div className={styles?.bg_frame}>{getIcon({ icon: "businessDecisions" })}</div>
			<Head>{PageTitles.CREATE_ACCOUNT}</Head>
			<div className={styles?.root}>
				<HeaderStatusInfo message={StatusInfo.NO_ACTION_REQ} />
				<PageTitle showBackIcon={true} title={Constants.CREATE_ACCOUNT.CREATE_ACCOUNT_HEADER} subTitle={Constants.CREATE_ACCOUNT.CREATE_ACCOUNT_SUB_HEADER} goBack={goBack} />
				<div className={styles?.main_wrapper}>
					<Form className={styles?.form} layout="vertical">
						<Label label={Constants.CREATE_ACCOUNT.TENANT_NAME_LABEL} classes={{ label: styles?.name_label }} />
						<div className="my-[1.25rem]">
							<Select
								showSearch
								className={`left-nav-search ${styles.inputSearch}`}
								placeholder={Constants.CREATE_ACCOUNT.SEARCH_LABEL}
								data-testid="search_input"
								listHeight={325}
								onChange={onChange}
								onSearch={onSearch}
								value={searchInput}
								dropdownStyle={theme.dropdownStyle}
								suffixIcon={<span className="cursor-pointer">{getIcon({ icon: "search" })}</span>}
								filterOption={(input, option) => (option?.value as unknown as string).toLowerCase().includes(input.toLowerCase())}
							>
								{storesDataList.map((item) => {
									return (
										<Option value={`${item.tenant_name}*${item.outlet_code}*${item.tenant_code}`} key={item.tenant_code}>
											{`${item.tenant_name} ${item.tenant_code}`}
										</Option>
									);
								})}
							</Select>
						</div>
						<Label label={Constants.CREATE_ACCOUNT.FILL_LABEL} classes={{ label: styles?.fill_label }} />
						<div>
							<Form.Item
								name="username"
								className="mb-auto"
								extra={<ErrorMessageWrapper fieldError={fieldErrors?.username} message={Constants.CREATE_ACCOUNT.INVALID_USERNAME} />}
								label={<Label label={Constants.CREATE_ACCOUNT.USERNAME_LABEL} classes={{ label: styles?.basic_input_label }} />}
							>
								<Input
									placeholder={Constants.CREATE_ACCOUNT.USERNAME_INPUT_PLACEHOLDER}
									className={`${styles.input_field} ${adminData?.username ? usernameClasses : ""}`}
									onChange={handleChange}
									value={adminData.username}
									onBlur={() => validateFields(false)}
									data-testid="username"
								/>
							</Form.Item>
							<Form.Item
								className="mb-auto"
								extra={<ErrorMessageWrapper fieldError={fieldErrors?.email} message={Constants.CREATE_ACCOUNT.INVALID_EMAIL} />}
								name="email"
								label={<Label label={Constants.CREATE_ACCOUNT.EMAIL_LABEL} classes={{ label: styles?.basic_input_label }} />}
							>
								<Input
									placeholder={Constants.CREATE_ACCOUNT.EMAIL_INPUT_PLACEHOLDER}
									className={`${styles.input_field} ${adminData?.email ? emailClasses : ""}`}
									onChange={handleChange}
									value={adminData.email}
									onBlur={() => validateFields(false)}
									data-testid="email"
								/>
							</Form.Item>
						</div>
						<div className={styles.button_wrapper}>
							<Button
								label={Constants.CREATE_ACCOUNT.BUTTON_LABEL}
								type={isCreateAdminButtonEnabled && !fieldErrors.email && !fieldErrors.username ? "activeLong" : "disabledLong"}
								onClick={proceedToCreateAdmin}
							/>
						</div>
					</Form>
				</div>
			</div>
		</div>
	);
};

export default CreateAccountPage;
